import { Alert, App, Input, Modal, Radio, Select, Space, Table, Tag, Typography } from "antd";
import { useEffect, useMemo, useState } from "react";

import { fetchGroupCandidates, useSetEnabledByGroup } from "../../api/candidates";
import { useNames } from "../../api/names";
import type { CandidateSummary, Group } from "../../api/types";
import { useAuthenticated, usePermission } from "../../auth/SessionProvider";

/**
 * Close (or reopen) every account of one group in the caller's scope, e.g. all
 * candidates once the exam is over. Ticked candidates are excluded, which is
 * how extended-time candidates stay open; the keyword box finds them quickly.
 */
export function GroupBatchModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { token } = useAuthenticated();
  const { catalog, siteName, roomName } = useNames();
  const { message } = App.useApp();
  const mutation = useSetEnabledByGroup();
  const canDisable = usePermission("candidate:disable");
  const canEnable = usePermission("candidate:enable");
  const groups = catalog.data?.groups ?? [];
  const [group, setGroup] = useState<Group>();
  const [enabled, setEnabled] = useState(false);
  const [candidates, setCandidates] = useState<CandidateSummary[]>();
  const [loading, setLoading] = useState(false);
  const [excluded, setExcluded] = useState<string[]>([]);
  const [keyword, setKeyword] = useState("");
  const [error, setError] = useState<string>();

  useEffect(() => {
    if (open) {
      setGroup(groups[0]?.id);
      setExcluded([]);
      setCandidates(undefined);
      setKeyword("");
      setError(undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open || group === undefined) return;
    let cancelled = false;
    setLoading(true);
    fetchGroupCandidates(token, group)
      .then((items) => { if (!cancelled) setCandidates(items); })
      .catch((caught: unknown) => { if (!cancelled) setError(caught instanceof Error ? caught.message : "加载失败"); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, group, token]);

  const affected = (candidates ?? []).filter((item) => !excluded.includes(item.username) && item.enabled !== enabled);
  const shown = useMemo(() => {
    const text = keyword.trim().toLowerCase();
    if (!text) return candidates ?? [];
    return (candidates ?? []).filter((item) => item.username.toLowerCase().includes(text) || item.name.toLowerCase().includes(text) || siteName(item.siteId).includes(text));
  }, [candidates, keyword, siteName]);
  const groupLabel = groups.find((item) => item.id === group)?.name ?? "";

  const submit = async () => {
    if (group === undefined) return;
    try {
      const result = await mutation.mutateAsync({ group, enabled, exclude: excluded });
      message.success(`${enabled ? "开放" : "关闭"}了 ${result.updated} 个账号，排除 ${result.excluded.length} 个，${result.unchanged} 个状态未变${result.failed.length ? `，失败 ${result.failed.length} 个` : ""}`);
      onClose();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "操作失败");
    }
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      onOk={() => void submit()}
      okText={`确认${enabled ? "开放" : "关闭"}${groupLabel} ${affected.length} 个账号`}
      okButtonProps={{ danger: !enabled, disabled: group === undefined || affected.length === 0 || (enabled ? !canEnable : !canDisable) }}
      confirmLoading={mutation.isPending}
      title="按组别批量操作"
      width={820}
    >
      <Space direction="vertical" size="middle" style={{ width: "100%" }}>
        <Space wrap>
          <Select<Group>
            style={{ width: 160 }}
            value={group}
            onChange={(value) => { setGroup(value); setExcluded([]); }}
            options={groups.map((item) => ({ value: item.id, label: item.name }))}
          />
          <Radio.Group value={enabled} onChange={(event) => setEnabled(event.target.value as boolean)}>
            <Radio.Button value={false}>关闭账号</Radio.Button>
            <Radio.Button value={true}>开放账号</Radio.Button>
          </Radio.Group>
          <Input.Search allowClear placeholder="按准考证号、姓名或考点查找要排除的考生" style={{ width: 320 }} value={keyword} onChange={(event) => setKeyword(event.target.value)} />
        </Space>
        {error && <Alert type="error" showIcon message={error} />}
        {!enabled && <Alert type="warning" showIcon message="关闭后该组考生不能再登录，已登录的考生下一次提交会失败。请把仍在答题的延时考生勾选排除。" />}
        <Typography.Text type="secondary">
          勾选需要排除的考生（例如延时考生），未勾选且状态需要改变的账号将被{enabled ? "开放" : "关闭"}。已排除 {excluded.length} 人。
        </Typography.Text>
        <Table<CandidateSummary>
          rowKey="username"
          size="small"
          loading={loading}
          dataSource={shown}
          pagination={{ pageSize: 20, size: "small", showTotal: (total) => `共 ${total} 人` }}
          rowSelection={{
            selectedRowKeys: excluded,
            preserveSelectedRowKeys: true,
            onChange: (keys) => setExcluded(keys as string[]),
            columnTitle: "排除"
          }}
          columns={[
            { title: "准考证号", dataIndex: "username", sorter: (a, b) => a.username.localeCompare(b.username), defaultSortOrder: "ascend" },
            { title: "姓名", dataIndex: "name" },
            { title: "考点", dataIndex: "siteId", render: siteName },
            { title: "考场", dataIndex: "roomId", render: roomName },
            { title: "状态", render: (_, row) => row.enabled ? <Tag color="green">启用</Tag> : <Tag color="red">已关闭</Tag> }
          ]}
        />
        <Typography.Text>
          {groupLabel}共 {candidates?.length ?? 0} 人，将{enabled ? "开放" : "关闭"} <strong>{affected.length}</strong> 个账号。
        </Typography.Text>
      </Space>
    </Modal>
  );
}
